import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { isAdmin } from "@app/_helpers/permission";
import { environment } from "@env/environment";
import { IsBetaWindow } from "@env/version";
import { UserSettingsQuery } from "@timeghost/web-api";

@Injectable({
	providedIn: "root",
})
export class BetaRedirectService {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private router: Router
	) {}

	/**
	 * Checks if the current user should be redirected from beta to production
	 * @returns true if user should be redirected, false otherwise
	 */
	shouldRedirectToProduction(): boolean {
		// Only check if we're in a beta environment
		const isBetaEnvironment = !environment.production || IsBetaWindow();

		if (!isBetaEnvironment) {
			return false;
		}

		// Get user settings
		const userSettings = this.userSettingsQuery.getValue();

		if (!userSettings?.workspace) {
			return false;
		}

		// Admins always have access to beta features
		if (isAdmin(userSettings)) {
			return false;
		}

		// For non-admins, check the workspace setting
		const nonAdminsCanAccessBeta = userSettings.workspace.settings?.nonAdminsCanAccessBeta;

		// If the setting is explicitly set to false, non-admins should be redirected
		return nonAdminsCanAccessBeta === false;
	}

	/**
	 * Redirects the user to the production environment
	 * @param currentUrl The current URL to redirect from
	 */
	redirectToProduction(currentUrl?: string): void {
		const url = currentUrl || window.location.pathname + window.location.search + window.location.hash;

		// If we're on web-beta.timeghost.io, redirect to web.timeghost.io
		if (window.location.hostname === "web-beta.timeghost.io") {
			const productionHostname = "web.timeghost.io";
			const productionUrl = `https://${productionHostname}${url}`;
			window.location.href = productionUrl;
		} else {
			// For other beta environments, try to navigate to the same route
			this.router.navigate([url]);
		}
	}

	/**
	 * Handles deep link access to beta features
	 * @param url The URL being accessed
	 * @returns true if access is allowed, false if redirect is needed
	 */
	handleDeepLinkAccess(url: string): boolean {
		if (this.shouldRedirectToProduction()) {
			this.redirectToProduction(url);
			return false;
		}
		return true;
	}
}
